<template>
  <div class="space-y-24">
    <div v-if="businessDetails">
      <div class="mb-16 flex items-center justify-between">
        <h2 class="heading-2">
          {{ i18n(translations.businessDetailsTitle) }}
        </h2>
        <RevButton
          v-if="isEditable"
          :icon="IconEdit"
          size="small"
          :to="{ name: ROUTE_NAMES.ONBOARDING.KYB.STEPS.STEP_1 }"
          variant="secondary"
          with-icon
        >
          {{ i18n(translations.businessDetailsEditButton) }}
        </RevButton>
      </div>

      <div class="space-y-16">
        <div class="grid grid-cols-4 gap-16">
          <p class="text-static-default-mid body-1 col-span-1">
            {{ i18n(translations.businessDetailsLegalName) }}
          </p>
          <p class="body-1 col-span-3">{{ businessDetails?.legalName }}</p>
        </div>

        <div class="grid grid-cols-4 gap-16">
          <p class="text-static-default-mid body-1 col-span-1">
            {{ i18n(translations.businessDetailsTradeName) }}
          </p>
          <p class="body-1 col-span-3">{{ businessDetails?.tradeName }}</p>
        </div>

        <div class="grid grid-cols-4 gap-16">
          <p class="text-static-default-mid body-1 col-span-1">
            {{ i18n(translations.businessDetailsRegistrationCountry) }}
          </p>
          <p class="body-1 col-span-3">
            {{ i18n.country(businessDetails.registrationCountry) }}
          </p>
        </div>

        <div class="grid grid-cols-4 gap-16">
          <p class="text-static-default-mid body-1 col-span-1">
            {{ i18n(translations.businessDetailsRegistrationType) }}
          </p>
          <p class="body-1 col-span-3">
            {{ businessDetails?.registrationType }}
          </p>
        </div>

        <div class="grid grid-cols-4 gap-16">
          <p class="text-static-default-mid body-1 col-span-1">
            {{ i18n(translations.businessDetailsRegistrationNumber) }}
          </p>
          <p class="body-1 col-span-3">
            {{ businessDetails?.registrationNumber }}
          </p>
        </div>

        <div class="grid grid-cols-4 gap-16">
          <p class="text-static-default-mid body-1 col-span-1">
            {{ i18n(translations.businessDetailsIncorporationDate) }}
          </p>
          <p class="body-1 col-span-3">
            {{ formatDate(businessDetails?.incorporationDate) }}
          </p>
        </div>
      </div>

      <div class="mt-16 grid grid-cols-4 gap-16">
        <p class="text-static-default-mid body-1 col-span-1">
          {{ i18n(translations.businessDetailsHeadquartersAddress) }}
        </p>
        <p class="body-1 col-span-3">
          {{ formatAddress(businessDetails?.headquartersAddress) }}
        </p>
      </div>
      <div class="mt-16 grid grid-cols-4 gap-16">
        <p class="text-static-default-mid body-1 col-span-1">
          {{ i18n(translations.certificateOfIncorporation) }}
        </p>
        <p class="body-1 col-span-3 flex items-center gap-2">
          <IconAttachment class="mr-2" />
          {{ businessDetails?.articleOfIncorporation?.filename }}
        </p>
      </div>
      <div class="mt-16 grid grid-cols-4 gap-16">
        <p class="text-static-default-mid body-1 col-span-1">
          {{ i18n(translations.businessDetailsInvestedCapitalCurrency) }}
        </p>
        <p class="body-1 col-span-3">
          {{ businessDetails?.investedCapital?.currency }}
        </p>
      </div>
      <div class="mt-16 grid grid-cols-4 gap-16">
        <p class="text-static-default-mid body-1 col-span-1">
          {{ i18n(translations.businessDetailsInvestedCapital) }}
        </p>
        <p class="body-1 col-span-3">
          {{ businessDetails?.investedCapital?.amount }}
        </p>
      </div>
    </div>

    <div>
      <div class="mb-16 flex items-center justify-between">
        <h2 class="heading-2">
          {{ i18n(translations.accountRequesterTitle) }}
        </h2>
        <RevButton
          v-if="isEditable"
          :icon="IconEdit"
          size="small"
          :to="{ name: ROUTE_NAMES.ONBOARDING.KYB.STEPS.STEP_2 }"
          variant="secondary"
          with-icon
        >
          {{ i18n(translations.accountRequesterEditButton) }}
        </RevButton>
      </div>

      <StakeholderInfo
        v-if="accountRequester"
        :stakeholder="accountRequester"
      />
    </div>

    <div>
      <div class="mb-16 flex items-center justify-between">
        <h2 class="heading-2">
          {{ i18n(translations.legalRepresentativeTitle) }}
        </h2>
        <RevButton
          v-if="isEditable"
          :icon="IconEdit"
          size="small"
          :to="{ name: ROUTE_NAMES.ONBOARDING.KYB.STEPS.STEP_3 }"
          variant="secondary"
          with-icon
        >
          {{ i18n(translations.legalRepresentativeEditButton) }}
        </RevButton>
      </div>
      <StakeholderInfo
        v-if="legalRepresentative"
        :stakeholder="legalRepresentative"
      />
    </div>

    <div>
      <div class="mb-16 flex items-center justify-between">
        <h2 class="heading-2">
          {{ i18n(translations.beneficialOwnerPrimaryOwner) }}
        </h2>
        <RevButton
          v-if="isEditable"
          :icon="IconEdit"
          size="small"
          :to="{ name: ROUTE_NAMES.ONBOARDING.KYB.STEPS.STEP_4 }"
          variant="secondary"
          with-icon
        >
          {{ i18n(translations.beneficialOwnerEditButton) }}
        </RevButton>
      </div>

      <StakeholderInfo :stakeholder="primaryBeneficialOwner!" />
    </div>

    <div
      v-for="secondaryBeneficialOwner in secondaryBeneficialOwners"
      :key="secondaryBeneficialOwner.id!"
    >
      <div class="mb-16 flex items-center justify-between">
        <h2 class="heading-2">
          {{ i18n(translations.otherBeneficialOwnerOwnerDetails) }}
        </h2>
        <RevButton
          v-if="isEditable"
          :icon="IconEdit"
          size="small"
          :to="{ name: ROUTE_NAMES.ONBOARDING.KYB.STEPS.STEP_4 }"
          variant="secondary"
          with-icon
        >
          {{ i18n(translations.beneficialOwnerEditButton) }}
        </RevButton>
      </div>

      <StakeholderInfo :stakeholder="secondaryBeneficialOwner" />
    </div>

    <div v-if="letterOfAuthorization">
      <div class="mb-16 flex items-center justify-between">
        <h2 class="heading-2">
          {{ i18n(translations.letterOfAuthorizationTitle) }}
        </h2>
        <RevButton
          v-if="isEditable"
          :icon="IconEdit"
          size="small"
          :to="{ name: ROUTE_NAMES.ONBOARDING.KYB.STEPS.STEP_5 }"
          variant="secondary"
          with-icon
        >
          {{ i18n(translations.letterOfAuthorizationEditButton) }}
        </RevButton>
      </div>

      <p class="flex items-center gap-2">
        <IconAttachment class="mr-2" /> {{ letterOfAuthorization.filename }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { IconAttachment } from '@ds/icons/IconAttachment'
import { IconEdit } from '@ds/icons/IconEdit'

import { ROUTE_NAMES } from '~/constants/routes'
import { useKybData } from '~/scopes/kyb/composables/useKybData'

import StakeholderInfo from './StakeholderInfo.vue'
import translations from './Summary.translations'

withDefaults(
  defineProps<{
    isEditable?: boolean
  }>(),
  {
    isEditable: false,
  },
)

const {
  businessDetails,
  accountRequester,
  legalRepresentative,
  primaryBeneficialOwner,
  secondaryBeneficialOwners,
  letterOfAuthorization,
} = useKybData()

const i18n = useI18n()

function formatDate(dateString?: string): string {
  if (!dateString) return ''

  return new Date(dateString).toLocaleDateString()
}

function formatAddress(address?: {
  addressLine1: string
  addressLine2?: string | null
  postalCode: string
  city: string
  country: string
}): string {
  if (!address) return ''

  const parts = [
    address.addressLine1,
    address.addressLine2,
    `${address.postalCode} ${address.city}`,
    address.country,
  ].filter(Boolean)

  return parts.join(', ')
}
</script>
